import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../../../../../redux/initialStoreState";
import { PageLoader } from "../../../../../../../components/PageLoader/PageLoader";
import { LoadState, MODULE_IDS } from "../../../../../../../constants/enums";
import { StandadCard } from "../../../../../../../components/Cards";
import {
  Box,
  Button,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import { CustomTextField } from "../../../../../../../components/formsComponents";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useDispatchWrapper } from "../../../../../../../hooks";
import { useFormik } from "formik";
import { LabelCard } from "../../../../../../../components/LabelCard/LabelCard";
import produce from "immer";
import {
  addLifeInsuranceCoverageNeedAsync,
  fetchLifeInsuranceCoverageNeedAsync,
  cleaLifeInsuranceCoverageNeed,
} from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceCoverageNeed/lifeInsuranceCoverageNeedActions";
import { ILifeInsuranceCoverageNeed } from "../../../../../../../redux/lifeInsurancePolicy/lifeInsuranceCoverageNeed/lifeInsuranceCoverageNeed.types";
import { RoleBasedCustomButton } from "../../../../../../../security/RoleBasedComponents/RoleBasedCustomButton/RoleBasedCustomButton";

export const LifeInsuranceCoverageNeed: React.FC = () => {
  const { customerId, customerPolicyId, incremental_number } = useParams() as {
    customerId?: number;
    customerPolicyId?: number;
    incremental_number?: string;
  };

  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Mode detection:
  // - incremental_number present + viewMode=true → view (read-only) mode
  // - incremental_number present → edit mode
  // - path ends with /create (no incremental_number) → create mode
  const viewMode = searchParams.get("viewMode") === "true";
  const isEditMode = !!incremental_number && !viewMode;
  const isViewMode = !!incremental_number && viewMode;
  const isCreateMode = !incremental_number && location.pathname.endsWith("/create");

  const { loading, data, error } = useSelector(
    (storeState: IStoreState) => storeState.lifeInsurance.coverageNeed
  );
  const [saveLoading, setSaveLoading] = React.useState(false);
  const dispatch = useDispatchWrapper();
  const navigate = useNavigate();

  const {
    values,
    errors,
    handleChange,
    handleSubmit,
    setFieldValue,
    setValues,
  } = useFormik({
    initialValues: data,
    enableReinitialize: true,
    validate: (values) => {},
    onSubmit: (values) => {
      if (customerPolicyId && customerId) {
        setSaveLoading(true);
        dispatch(
          addLifeInsuranceCoverageNeedAsync(
            {
              ...values,
              customer_policy_id: customerPolicyId,
              incremental_number: incremental_number ? Number(incremental_number) : undefined,
              rider_code: incremental_number ? values.rider_code : undefined,
              section_a_total: a,
              section_b_capital_required: b,
              section_c_total: c,
              life_insurance_needs_total: total,
            } as ILifeInsuranceCoverageNeed,
            (isSuccess) => {
              if (isSuccess) {
                navigate(
                  `/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}`
                );
              }
              setSaveLoading(false);
            }
          )
        );
      }
    },
  });

  const a = React.useMemo(() => {
    if (values.identify_current_debts_and_financial_needs) {
      return values.identify_current_debts_and_financial_needs.reduce(
        (item, currentValue) => {
          return item + (Number(currentValue.amount) || 0);
        },
        0
      );
    }
    return 0;
  }, [values.identify_current_debts_and_financial_needs]);

  const b = React.useMemo(() => {
    if (values.determine_your_family_s_future_financial_needs) {
      const data = values.determine_your_family_s_future_financial_needs;

      const srNo7Object = data.find((item) => item.SrNo === "7");
      const srNo8Object = data.find((item) => item.SrNo === "8");
      if (
        srNo7Object &&
        srNo8Object &&
        srNo7Object.amount !== null &&
        srNo8Object.amount !== null
      ) {
        const product = Number(srNo7Object.amount) * Number(srNo8Object.amount);
        return product;
      }
    }
    return 0;
  }, [values.determine_your_family_s_future_financial_needs]);

  const c = React.useMemo(() => {
    if (values.deduct_your_assets_and_financial_resources) {
      return values.deduct_your_assets_and_financial_resources.reduce(
        (item, currentValue) => {
          return item + (Number(currentValue.amount) || 0);
        },
        0
      );
    }
    return 0;
  }, [values.deduct_your_assets_and_financial_resources]);

  const total = a + b - c;

  const handleIdentifyCurrentDebtsChange =
    (rowIndex: number, key: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const finalRows = produce(
        values.identify_current_debts_and_financial_needs,
        (draftRows: any) => {
          draftRows[rowIndex][key] = event.target.value;
        }
      );
      setValues({
        ...values,
        identify_current_debts_and_financial_needs: finalRows,
      });
    };

  const handleDetermineYourFamilyChange =
    (rowIndex: number, key: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const finalRows = produce(
        values.determine_your_family_s_future_financial_needs,
        (draftRows: any) => {
          draftRows[rowIndex][key] = event.target.value;
        }
      );
      setValues({
        ...values,
        determine_your_family_s_future_financial_needs: finalRows,
      });
    };

  const handleDetermineYourFamilyChangeSrNoSeven = () => {
    const finalRows = produce(
      values.determine_your_family_s_future_financial_needs,
      (draftRows: any) => {
        draftRows["3"]["amount"] =
          draftRows["1"]["amount"] - draftRows["2"]["amount"];
      }
    );
    setValues({
      ...values,
      determine_your_family_s_future_financial_needs: finalRows,
    });
  };

  React.useMemo(() => {
    handleDetermineYourFamilyChangeSrNoSeven();
  }, [values.determine_your_family_s_future_financial_needs]);

  const handleDeductYourAssetsChange =
    (rowIndex: number, key: string) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const finalRows = produce(
        values.deduct_your_assets_and_financial_resources,
        (draftRows: any) => {
          draftRows[rowIndex][key] = event.target.value;
        }
      );
      setValues({
        ...values,
        deduct_your_assets_and_financial_resources: finalRows,
      });
    };

  React.useEffect(() => {
    // Always clear state first to ensure a fresh form
    dispatch(cleaLifeInsuranceCoverageNeed());
    if (customerPolicyId && incremental_number) {
      dispatch(
        fetchLifeInsuranceCoverageNeedAsync(
          customerPolicyId,
          Number(incremental_number)
        )
      );
    }
    return () => {
      // Clear state on unmount so navigating to create starts fresh
      dispatch(cleaLifeInsuranceCoverageNeed());
    };
  }, [customerPolicyId, incremental_number]);

  return (
    <PageLoader
      loading={loading === LoadState.InProgress}
      error={error ? { message: error } : null}
    >
      <StandadCard sx={{ mt: 2 }}>
        <form onSubmit={handleSubmit}>
          <LabelCard heading="A. Indentify your currents debts and financial needs" />
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableBody>
              {values.identify_current_debts_and_financial_needs.map(
                (item, index) => {
                  return (
                    <TableRow
                      key={item.key}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                      }}
                    >
                      {item.typed === true ? (
                        <TableCell sx={{ width: "70%" }}>
                          {" "}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>{item.SrNo}</Typography>
                            <CustomTextField
                              name="item.debt"
                              variant="outlined"
                              size="small"
                              type="text"
                              fullWidth
                              disabled={isViewMode}
                              value={item.debt}
                              onChange={handleIdentifyCurrentDebtsChange(
                                index,
                                "debt"
                              )}
                            />
                          </Stack>
                        </TableCell>
                      ) : (
                        <TableCell sx={{ width: "70%" }}>
                          {" "}
                          <Typography>
                            {item.SrNo}. {item.debt}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        {" "}
                        <CustomTextField
                          name="item.amount"
                          variant="outlined"
                          size="small"
                          type="text"
                          fullWidth
                          disabled={isViewMode}
                          value={item.amount}
                          onChange={handleIdentifyCurrentDebtsChange(
                            index,
                            "amount"
                          )}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <FileUpload value={""} />
                      </TableCell> */}
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Typography variant="h4" fontWeight={600}>
                Total
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h4" fontWeight={600}>
                $ {a}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <LabelCard heading="B. Determine your Family's future Income needs after your death" />
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableBody>
              {values.determine_your_family_s_future_financial_needs.map(
                (item, index) => {
                  return (
                    <TableRow
                      key={item.key}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      {item.debt === "" ? (
                        <TableCell sx={{ width: "70%" }}>
                          {" "}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>{item.SrNo} </Typography>
                            <CustomTextField
                              name="country"
                              variant="outlined"
                              size="small"
                              type="text"
                              fullWidth
                              disabled={isViewMode}
                              value={""}
                              onChange={handleChange}
                            />
                          </Stack>
                        </TableCell>
                      ) : (
                        <TableCell sx={{ width: "70%" }}>
                          {" "}
                          <Typography>
                            {item.SrNo}. {item.debt}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        {" "}
                        <CustomTextField
                          name="item.amount"
                          variant="outlined"
                          size="small"
                          type="text"
                          fullWidth
                          disabled={isViewMode}
                          value={item.amount}
                          onChange={handleDetermineYourFamilyChange(
                            index,
                            "amount"
                          )}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <FileUpload value={""} />
                      </TableCell> */}
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Typography variant="h4" fontWeight={600}>
                Capital Required
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h4" fontWeight={600}>
                $ {b}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <LabelCard heading="C. Deduct your assets and financial resources" />
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableBody>
              {values.deduct_your_assets_and_financial_resources.map(
                (item, index) => {
                  return (
                    <TableRow
                      key={item.key}
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      {item.debt === "" ? (
                        <TableCell sx={{ width: "70%" }}>
                          {" "}
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                          >
                            <Typography>{item.SrNo} </Typography>
                            <CustomTextField
                              name="country"
                              variant="outlined"
                              size="small"
                              type="text"
                              fullWidth
                              disabled={isViewMode}
                              value={""}
                              onChange={handleChange}
                            />
                          </Stack>
                        </TableCell>
                      ) : (
                        <TableCell sx={{ width: "70%" }}>
                          {" "}
                          <Typography>
                            {item.SrNo}. {item.debt}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell>
                        {" "}
                        <CustomTextField
                          name="item.amount"
                          variant="outlined"
                          size="small"
                          type="text"
                          fullWidth
                          disabled={isViewMode}
                          value={item.amount}
                          onChange={handleDeductYourAssetsChange(
                            index,
                            "amount"
                          )}
                        />
                      </TableCell>
                      {/* <TableCell>
                        <FileUpload value={""} />
                      </TableCell> */}
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </Table>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Typography variant="h4" fontWeight={600}>
                Total
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h4" fontWeight={600}>
                $ {c}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2, mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={7}>
              <Typography variant="h4" fontWeight={600}>
                Your Life Insurance Needs = A+B-C
              </Typography>
            </Grid>
            <Grid item xs={3}>
              <Typography variant="h4" fontWeight={600}>
                $ {total}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 2, mb: 2 }} />

          {!isViewMode && (
            <Box sx={{ mt: 4 }}>
              <Stack direction="row" spacing={2}>
                <RoleBasedCustomButton
                  sx={{ width: "15%" }}
                  moduleKey={MODULE_IDS.LIFE_AND_FINCANCIAL}
                  disabled={saveLoading}
                  variant="contained"
                  type="submit"
                >
                  Save
                </RoleBasedCustomButton>
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigate(
                      `/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}`
                    )
                  }
                >
                  Back
                </Button>
              </Stack>
            </Box>
          )}
          {isViewMode && (
            <Box sx={{ mt: 4 }}>
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/life-insurance-policy/${customerId}/coverage-needs-tab/${customerPolicyId}`
                  )
                }
              >
                Back
              </Button>
            </Box>
          )}
        </form>
      </StandadCard>
    </PageLoader>
  );
};
